// 안전한 에러 경계 컴포넌트 (크래시 방지 + 사용자 친화적 복구)
import React, { Component, ReactNode } from 'react';
import { Box, Typography, Button, Alert } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface SafeErrorBoundaryProps {
  children: ReactNode;
  step?: number;
  stepName?: string;
  onError?: (error: Error, errorInfo: any) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// 단계별 에러 폴백 컴포넌트
const StepErrorFallback = ({ error, onRetry, step, stepName }: {
  error: Error | null;
  onRetry: () => void;
  step?: number;
  stepName?: string;
}) => (
  <Box sx={{ 
    p: 4, 
    textAlign: 'center',
    background: 'linear-gradient(135deg, rgba(255, 243, 224, 0.8) 0%, rgba(255, 237, 213, 0.8) 100%)',
    borderRadius: 3,
    border: '1px solid rgba(251, 146, 60, 0.3)'
  }}>
    <ErrorOutlineIcon sx={{ 
      fontSize: 48, 
      color: '#ea580c', 
      mb: 2,
      opacity: 0.8 
    }} />
    
    <Typography variant="h6" fontWeight={700} color="#ea580c" mb={1}>
      {stepName || `${step}단계`}에서 일시적인 문제가 발생했습니다
    </Typography>
    
    <Typography variant="body2" color="#92400e" mb={3} sx={{ lineHeight: 1.6 }}>
      걱정하지 마세요! 데이터를 다시 불러와서 해결할 수 있습니다.<br/>
      <strong>기존 입력하신 정보는 그대로 유지됩니다.</strong>
    </Typography>
    
    <Button
      variant="contained"
      startIcon={<RefreshIcon />}
      onClick={onRetry}
      sx={{
        fontWeight: 700,
        borderRadius: 2,
        px: 3,
        py: 1.5,
        background: 'linear-gradient(135deg, #ea580c 0%, #dc2626 100%)',
        '&:hover': {
          background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
        }
      }}
    >
      다시 시도하기
    </Button>
    
    {process.env.NODE_ENV === 'development' && error && (
      <Alert severity="warning" sx={{ mt: 2, textAlign: 'left' }}>
        <Typography variant="body2" component="pre" sx={{ fontSize: '0.8rem', overflow: 'auto' }}>
          개발용 정보: {error.message}
        </Typography>
      </Alert>
    )}
  </Box>
);

export class SafeErrorBoundary extends Component<SafeErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: SafeErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    const { step, stepName, onError } = this.props;
    
    // 🔥 에러 로깅 (운영 환경에서는 Sentry 등으로 전송)
    console.error(`[SafeErrorBoundary] Step ${step || 'Unknown'} Error:`, {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      stepName: stepName || `Step ${step}`
    });
    
    // 커스텀 에러 핸들러 실행
    if (onError) {
      onError(error, errorInfo);
    }
    
    // 🚨 중요: 사용자 데이터 손실 방지를 위한 로컬스토리지 백업
    try {
      const currentSelections = {
        timestamp: Date.now(),
        error: error.message,
        step,
        stepName: stepName || `Step ${step}`,
        // 필요 시 현재 선택 상태들도 백업 가능
      };
      localStorage.setItem('device_dialog_error_backup', JSON.stringify(currentSelections));
    } catch (backupError) {
      console.warn('백업 저장 실패:', backupError);
    }
  }

  handleRetry = () => {
    console.log(`🔄 Step ${this.props.step || 'Unknown'} 에러 경계 리셋`);
    this.setState({ hasError: false, error: null });
    
    // 에러 백업 정리
    try {
      localStorage.removeItem('device_dialog_error_backup');
    } catch (cleanupError) {
      console.warn('백업 정리 실패:', cleanupError);
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <StepErrorFallback
          error={this.state.error}
          onRetry={this.handleRetry}
          step={this.props.step}
          stepName={this.props.stepName}
        />
      );
    }

    return this.props.children;
  }
}
